import React, { useEffect, useState } from "react";
import api, { inr } from "../../lib/api";
import { Plus, Edit3, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import ProductImagesEditor from "../../components/admin/ProductImagesEditor";

const EMPTY = {
    name: "", subtitle: "", shortDescription: "", longDescription: "",
    category: "dresses", price: 0, compareAtPrice: null,
    sizes: [], sizeVariants: ["XS", "S", "M", "L", "XL"], ageGroup: [], colors: [],
    fabric: "", careInstructions: "", washCare: "",
    shippingNote: "Free shipping on orders above ₹1499.", returnNote: "Easy 7-day returns.",
    images: [], featured: false, inStock: true, stockStatus: "in_stock",
    paymentLink: "https://rzp.io/i/loomara-demo", whatsappMessageTemplate: "",
    seoTitle: "", seoDescription: "",
};

export default function AdminProducts() {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [editing, setEditing] = useState(null);
    const [showForm, setShowForm] = useState(false);

    const load = () => {
        api.get("/products", { params: { limit: 100 } }).then(({ data }) => setProducts(data));
        api.get("/categories").then(({ data }) => setCategories(data));
    };

    useEffect(() => { document.title = "Admin Products | Loomara"; load(); }, []);

    const save = async (form) => {
        try {
            if (editing?.id) {
                await api.put(`/products/${editing.id}`, form);
                toast("Product updated");
            } else {
                await api.post("/products", form);
                toast("Product created");
            }
            setShowForm(false);
            setEditing(null);
            load();
        } catch (e) {
            toast(e.response?.data?.detail || "Save failed");
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.name}"?`)) return;
        await api.delete(`/products/${p.id}`);
        toast("Product deleted");
        load();
    };

    return (
        <div data-testid="admin-products-page">
            <div className="flex items-center justify-between">
                <div>
                    <p className="eyebrow">Catalogue</p>
                    <h1 className="font-serif text-4xl text-ink-900 mt-2">Products</h1>
                </div>
                <button onClick={() => { setEditing(EMPTY); setShowForm(true); }} className="btn-primary" data-testid="admin-new-product-btn">
                    <Plus size={14} /> New Product
                </button>
            </div>

            <div className="card-soft mt-8 overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-ivory-100 text-ink-700">
                        <tr>
                            <th className="text-left px-6 py-4 font-medium tracking-wider uppercase text-[11px]">Product</th>
                            <th className="text-left px-6 py-4 font-medium tracking-wider uppercase text-[11px]">Category</th>
                            <th className="text-left px-6 py-4 font-medium tracking-wider uppercase text-[11px]">Price</th>
                            <th className="text-left px-6 py-4 font-medium tracking-wider uppercase text-[11px]">Stock</th>
                            <th className="text-left px-6 py-4 font-medium tracking-wider uppercase text-[11px]">Featured</th>
                            <th className="px-6 py-4"></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-sage-300/20">
                        {products.map((p) => (
                            <tr key={p.id} className="hover:bg-ivory-100/50" data-testid={`admin-row-${p.slug}`}>
                                <td className="px-6 py-3 flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-xl bg-ivory-100 overflow-hidden shrink-0">
                                        <img src={p.images?.[0]} alt="" className="w-full h-full object-cover" />
                                    </div>
                                    <span className="text-ink-900">{p.name}</span>
                                </td>
                                <td className="px-6 py-3 text-ink-700">{p.category}</td>
                                <td className="px-6 py-3 text-ink-700">{inr(p.price)}</td>
                                <td className="px-6 py-3 text-ink-700">{p.stockStatus}</td>
                                <td className="px-6 py-3 text-ink-700">{p.featured ? "Yes" : "—"}</td>
                                <td className="px-6 py-3 flex gap-3 justify-end">
                                    <button onClick={() => { setEditing(p); setShowForm(true); }} className="text-sage-700 hover:text-sage-600" data-testid={`admin-edit-${p.slug}`}><Edit3 size={16} /></button>
                                    <button onClick={() => remove(p)} className="text-blush-400 hover:text-blush-500" data-testid={`admin-delete-${p.slug}`}><Trash2 size={16} /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showForm && (
                <ProductForm
                    initial={editing}
                    categories={categories}
                    onSave={save}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                />
            )}
        </div>
    );
}

function parseCsv(v) {
    return (v || "").split(",").map((s) => s.trim()).filter(Boolean);
}

function joinCsv(v) {
    return Array.isArray(v) ? v.join(", ") : (v || "");
}

function ProductForm({ initial, categories, onSave, onClose }) {
    const [form, setForm] = useState({
        ...EMPTY,
        ...initial,
        sizes: initial?.sizes || [],
        sizeVariants: initial?.sizeVariants || EMPTY.sizeVariants,
        ageGroup: initial?.ageGroup || [],
        colors: initial?.colors || [],
        images: initial?.images || [],
        inStock: initial?.inStock !== undefined ? initial.inStock : true,
    });
    const [sizesText, setSizesText] = useState(joinCsv(initial?.sizes));
    const [ageGroupText, setAgeGroupText] = useState(joinCsv(initial?.ageGroup));
    const [colorsText, setColorsText] = useState(joinCsv(initial?.colors));

    const set = (k, v) => setForm({ ...form, [k]: v });

    const handleSave = () => {
        onSave({
            ...form,
            sizes: parseCsv(sizesText),
            ageGroup: parseCsv(ageGroupText),
            colors: parseCsv(colorsText),
        });
    };

    return (
        <div className="fixed inset-0 z-[60] bg-ink-900/40 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="admin-product-form">
            <div className="bg-white rounded-3xl w-full max-w-3xl p-8 shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="font-serif text-3xl">{initial?.id ? "Edit Product" : "New Product"}</h2>
                    <button onClick={onClose}><X /></button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <Field
                        label="Name"
                        hint="Full product title shown on the product page and in search."
                        value={form.name}
                        onChange={(v) => set("name", v)}
                        testid="pf-name"
                    />
                    <Field
                        label="Subtitle"
                        hint="Optional one-line tagline under the name (e.g. fabric or style note)."
                        value={form.subtitle}
                        onChange={(v) => set("subtitle", v)}
                        testid="pf-subtitle"
                    />

                    <SelectField
                        label="Category"
                        hint="Controls which shop category this product appears under."
                        value={form.category}
                        onChange={(v) => set("category", v)}
                        options={categories.map((c) => ({ value: c.slug, label: c.name }))}
                        testid="pf-category"
                    />
                    <SelectField
                        label="Stock Status"
                        hint="Display label for stock. Use Out of Stock to hide Add to Bag."
                        value={form.stockStatus}
                        onChange={(v) => set("stockStatus", v)}
                        options={[
                            { value: "in_stock", label: "In Stock" },
                            { value: "low_stock", label: "Low Stock" },
                            { value: "out_of_stock", label: "Out of Stock" },
                        ]}
                        testid="pf-stock"
                    />

                    <Field
                        label="Price (₹)"
                        hint="Selling price in rupees. Shown on the shop and product page."
                        type="number"
                        value={form.price}
                        onChange={(v) => set("price", Number(v))}
                        testid="pf-price"
                    />
                    <Field
                        label="Compare At Price (₹)"
                        hint="Optional original price for sale badges. Leave empty if not on sale."
                        type="number"
                        value={form.compareAtPrice || ""}
                        onChange={(v) => set("compareAtPrice", v ? Number(v) : null)}
                        testid="pf-compare"
                    />

                    <Field
                        label="Sizes"
                        hint="Comma-separated sizes customers can pick at checkout. Example: 0-6M, 6-12M, 1-2Y, S, M"
                        value={sizesText}
                        onChange={setSizesText}
                        testid="pf-sizes"
                    />
                    <Field
                        label="Age Group"
                        hint="Comma-separated age ranges for shop filters. Example: 0-6M, 6-12M, 1-2Y, 2-3Y"
                        value={ageGroupText}
                        onChange={setAgeGroupText}
                        testid="pf-age"
                    />
                    <Field
                        label="Colors"
                        hint="Comma-separated color names for filters and display. Example: Peach, Blush, Ivory"
                        value={colorsText}
                        onChange={setColorsText}
                        testid="pf-colors"
                    />
                    <Field
                        label="Fabric"
                        hint="Material composition shown in product details (e.g. Cotton-linen blend)."
                        value={form.fabric}
                        onChange={(v) => set("fabric", v)}
                        testid="pf-fabric"
                    />

                    <ProductImagesEditor
                        images={form.images}
                        onChange={(images) => set("images", images)}
                    />

                    <div className="col-span-2">
                        <TextArea
                            label="Short Description"
                            hint="Brief summary shown below the price on the product page (1–2 sentences)."
                            value={form.shortDescription}
                            onChange={(v) => set("shortDescription", v)}
                            testid="pf-short"
                        />
                    </div>
                    <div className="col-span-2">
                        <TextArea
                            label="Long Description"
                            hint="Full product story, fit notes, and care details for the accordion section."
                            value={form.longDescription}
                            onChange={(v) => set("longDescription", v)}
                            testid="pf-long"
                        />
                    </div>
                    <Field
                        label="Payment Link"
                        hint="Razorpay payment link for this product. Used for direct-pay flows."
                        value={form.paymentLink}
                        onChange={(v) => set("paymentLink", v)}
                        testid="pf-paylink"
                    />
                    <div className="col-span-2">
                        <TextArea
                            label="WhatsApp Message Template"
                            hint="Pre-filled WhatsApp text. Use {product_name} and {price} as placeholders."
                            value={form.whatsappMessageTemplate}
                            onChange={(v) => set("whatsappMessageTemplate", v)}
                            testid="pf-wa"
                        />
                    </div>

                    <div className="col-span-2 space-y-3 pt-1">
                        <label className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={!!form.featured}
                                onChange={(e) => set("featured", e.target.checked)}
                                data-testid="pf-featured"
                            />
                            <span>
                                <span className="text-ink-900">Featured on homepage</span>
                                <span className="block text-xs text-ink-500 mt-0.5">Show this product in the homepage featured collection.</span>
                            </span>
                        </label>
                        <label className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-0.5"
                                checked={!!form.inStock}
                                onChange={(e) => set("inStock", e.target.checked)}
                                data-testid="pf-instock"
                            />
                            <span>
                                <span className="text-ink-900">In stock</span>
                                <span className="block text-xs text-ink-500 mt-0.5">Uncheck to disable Add to Bag and show Sold Out.</span>
                            </span>
                        </label>
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-8">
                    <button onClick={onClose} className="btn-secondary" data-testid="pf-cancel">Cancel</button>
                    <button onClick={handleSave} className="btn-primary" data-testid="pf-save">{initial?.id ? "Update" : "Create"}</button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">{label}</label>
            <input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid={testid} />
            {hint && <p className="mt-1.5 text-xs text-ink-500 leading-relaxed">{hint}</p>}
        </div>
    );
}

function TextArea({ label, hint, value, onChange, testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">{label}</label>
            <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid={testid} />
            {hint && <p className="mt-1.5 text-xs text-ink-500 leading-relaxed">{hint}</p>}
        </div>
    );
}

function SelectField({ label, hint, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-[0.22em] text-ink-500 mb-1.5">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-ivory-100 rounded-xl px-4 py-2.5 text-sm border border-sage-300/30 focus:outline-none focus:ring-2 focus:ring-sage-300" data-testid={testid}>
                {options.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
            </select>
            {hint && <p className="mt-1.5 text-xs text-ink-500 leading-relaxed">{hint}</p>}
        </div>
    );
}
